<script setup lang="ts">
import {
	IconArrowBack,
	IconArrowBackUp,
	IconArrowForwardUp,
	IconCloudUp,
	IconEye,
	IconEyeOff,
	IconFile,
	IconFileArrowRight,
	IconFileUpload,
	IconFileZip,
	IconRestore,
	IconSettings,
	IconVersions,
} from "@tabler/icons-vue";
import { MenubarMenu, SelectRoot as Select, SelectValue } from "reka-ui";
import Menubar from "@/components/ui/menubar/Menubar.vue";
import MenubarContent from "@/components/ui/menubar/MenubarContent.vue";
import MenubarItem from "@/components/ui/menubar/MenubarItem.vue";
import MenubarSeparator from "@/components/ui/menubar/MenubarSeparator.vue";
import MenubarShortcut from "@/components/ui/menubar/MenubarShortcut.vue";
import MenubarTrigger from "@/components/ui/menubar/MenubarTrigger.vue";
import SelectContent from "@/components/ui/select/SelectContent.vue";
import SelectItem from "@/components/ui/select/SelectItem.vue";
import SelectTrigger from "@/components/ui/select/SelectTrigger.vue";
import Separator from "@/components/ui/separator/Separator.vue";
import { useEditorStore } from "@/stores/editor";
import { useTopBarStore } from "@/stores/topBar";

const topBarStore = useTopBarStore();
const editorStore = useEditorStore();
</script>

<template>
	<Menubar
		v-if="$route.meta.showEditorMenuBar"
		class="border-none rounded-none"
	>
		<MenubarMenu>
			<MenubarTrigger>File</MenubarTrigger>
			<MenubarContent>
				<MenubarItem @click="topBarStore.editorMenuItemClicked('file-sync')">
					<IconCloudUp class="w-5 h-5 mr-2 text-muted-foreground" />
					Save to Cloud
					<MenubarShortcut> Ctrl + Shift + S </MenubarShortcut>
				</MenubarItem>
				<MenubarItem
					@click="topBarStore.editorMenuItemClicked('file-compress')"
				>
					<IconFileZip class="w-5 h-5 mr-2 text-muted-foreground" />
					Compress (Experimental)
				</MenubarItem>
				<MenubarItem @click="topBarStore.editorMenuItemClicked('file-publish')">
					<IconFileArrowRight class="w-5 h-5 mr-2 text-muted-foreground" />
					Publish
				</MenubarItem>
				<MenubarItem
					@click="topBarStore.editorMenuItemClicked('open-file-explorer')"
				>
					<IconFile class="w-5 h-5 mr-2 text-muted-foreground" />
					Open File Explorer
				</MenubarItem>
				<MenubarItem
					@click="topBarStore.editorMenuItemClicked('file-upload-matrix')"
				>
					<IconFileUpload class="w-5 h-5 mr-2 text-muted-foreground" />
					Upload matrix.json
				</MenubarItem>
				<MenubarSeparator />
				<MenubarItem @click="topBarStore.editorMenuItemClicked('file-return')">
					<IconArrowBack class="w-5 h-5 mr-2 text-muted-foreground" />
					Return to Workspace
				</MenubarItem>
			</MenubarContent>
		</MenubarMenu>
		<MenubarMenu>
			<MenubarTrigger>Edit</MenubarTrigger>
			<MenubarContent>
				<MenubarItem @click="topBarStore.editorMenuItemClicked('edit-undo')">
					<IconArrowBackUp class="w-5 h-5 mr-2 text-muted-foreground" />
					Undo
					<MenubarShortcut> Ctrl + Z </MenubarShortcut>
				</MenubarItem>
				<MenubarItem @click="topBarStore.editorMenuItemClicked('edit-redo')">
					<IconArrowForwardUp class="w-5 h-5 mr-2 text-muted-foreground" />
					Redo
				</MenubarItem>
				<MenubarSeparator />
				<MenubarItem @click="topBarStore.editorMenuItemClicked('edit-refresh')">
					<IconRestore class="w-5 h-5 mr-2 text-muted-foreground" />
					Reset
				</MenubarItem>
				<MenubarSeparator />
				<MenubarItem
					@click="topBarStore.editorMenuItemClicked('edit-settings')"
				>
					<IconSettings class="w-5 h-5 mr-2 text-muted-foreground" />
					Settings
				</MenubarItem>
			</MenubarContent>
		</MenubarMenu>
		<MenubarMenu>
			<MenubarTrigger>View</MenubarTrigger>
			<MenubarContent>
				<MenubarItem
					@click="
						topBarStore.editorMenuItemClicked('view-show-product-versions')
					"
				>
					<IconVersions class="w-5 h-5 mr-2 text-muted-foreground" />
					Show Product Versions
				</MenubarItem>
				<MenubarItem @click="topBarStore.toggleSideNav()">
					<IconEye
						class="w-5 h-5 mr-2 text-muted-foreground"
						v-if="topBarStore.hideSideNav"
					/>
					<IconEyeOff class="w-5 h-5 mr-2 text-muted-foreground" v-else />
					{{ topBarStore.hideSideNav ? "Show" : "Hide" }} Sidebar
					<MenubarShortcut> Ctrl + Alt + S </MenubarShortcut>
				</MenubarItem>

				<MenubarItem
					@click="topBarStore.editorMenuItemClicked('toggle-fps-counter')"
				>
					<IconEye
						class="w-5 h-5 mr-2 text-muted-foreground"
						v-if="topBarStore.hideFpsCounter"
					/>
					<IconEyeOff class="w-5 h-5 mr-2 text-muted-foreground" v-else />
					{{ topBarStore.hideFpsCounter ? "Show" : "Hide" }} FPS Counter
				</MenubarItem>
			</MenubarContent>
		</MenubarMenu>
		<Separator orientation="vertical" class="mx-2 h-12" />
		<MenubarMenu>
			<Select v-model="editorStore.mode">
				<SelectTrigger class="h-8 w-36 -mx-1">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="edit"> Edit Mode </SelectItem>
					<SelectItem value="config"> Config Mode </SelectItem>
					<SelectItem value="preview"> Preview Mode </SelectItem>
				</SelectContent>
			</Select>
		</MenubarMenu>
	</Menubar>
</template>
